/**
 * Pure trading math shared by the order engine and the UI.
 * Everything takes and returns `D` (bigint fixed-point); no floats.
 *
 * Conventions
 *  - `feeRate` is a fraction (0.0005 = 0.05 %); `feeFlat` is USD, charged once on open.
 *  - Swap rates are percent per charge (-0.0125 = client pays 0.0125 % of notional).
 *  - Margin is cross: an account's positions share its equity, and the account stop-out level is a
 *    fraction of margin used (0.3 = stop-out at 30 % margin level, see accountStopOut).
 *  - `swap` on a position is the signed swap P&L accrued so far (negative = cost).
 */
import { D } from './decimal.js';
/** Money (USD) precision used for balances, fees, margin and PnL. */
export const MONEY_DP = 8;
/** Internal quantity precision. */
export const QTY_DP = 12;
const BPS = D.from(10000);
const HUNDRED = D.from(100);
const TWO = D.from(2);
/**
 * USD value of a 1.0 price move per unit of qty: contract size × the price currency's USD rate.
 * Pass it wherever trading math takes a contract size, so notional, P&L and margin come out in USD.
 */
export function usdPerPoint(contractSize, fx) {
    return fx === undefined || fx === null ? D.from(contractSize) : D.from(contractSize).mul(fx);
}
/**
 * One pip in price units: ten of the smallest price step (0.0001 for a 5-decimal pair, 0.01 for a
 * 3-decimal JPY pair).
 */
export function pipSize(priceDp) {
    return D.ONE.div(D.from(10n ** BigInt(Math.max(0, priceDp - 1))));
}
/**
 * Apply the instrument's minimum spread and markup to a raw feed quote.
 * mark = raw mid; half-spread = max(raw half-spread, mark·spreadBps/2) + mark·markupBps.
 * With `spreadPips` set the spread is fixed instead: half-spread = spreadPips·pip/2, whatever the
 * raw spread, minimum and markup are.
 */
export function applySpread(rawBid, rawAsk, spreadBps, markupBps, priceDp, spreadPips) {
    const rb = D.from(rawBid);
    const ra = D.from(rawAsk);
    const mark = rb.add(ra).div(TWO);
    let half;
    if (spreadPips !== undefined && spreadPips !== null) {
        half = D.from(spreadPips).mul(pipSize(priceDp)).div(TWO);
    }
    else {
        const rawHalf = ra.sub(rb).div(TWO).abs();
        const minHalf = mark.mul(spreadBps).div(BPS).div(TWO);
        half = D.max(rawHalf, minHalf).add(mark.mul(markupBps).div(BPS));
    }
    const bid = mark.sub(half).round(priceDp, 'floor');
    let ask = mark.add(half).round(priceDp, 'ceil');
    if (ask.lte(bid))
        ask = bid.add(D.ONE.div(D.from(10n ** BigInt(priceDp))));
    return { bid, ask, mark: mark.round(priceDp + 2, 'half_up') };
}
/** USD the client paid in spread on one fill: distance from the quote's mid, times size and point value. */
export function spreadCost(price, mark, qty, pointValue) {
    return D.from(price).sub(mark).abs().mul(qty).mul(pointValue);
}
/** Fill price for opening: longs buy at ask, shorts sell at bid. */
export function openPrice(side, q) {
    return side === 'long' ? q.ask : q.bid;
}
/** Fill price for closing: longs sell at bid, shorts buy back at ask. */
export function closePrice(side, q) {
    return side === 'long' ? q.bid : q.ask;
}
export function qtyFromNotional(notional, price, contractSize) {
    return D.from(notional).div(D.from(price).mul(contractSize), 'down').round(QTY_DP, 'down');
}
/** Qty for an order placed by volume: lots × lot size. */
export function lotsToQty(lots, lotSize) {
    return D.from(lots).mul(lotSize).round(QTY_DP, 'down');
}
/** Lots held by a qty (display): qty / lot size. */
export function qtyToLots(qty, lotSize) {
    return D.from(qty).div(lotSize, 'down').round(QTY_DP, 'down');
}
/** Round down to a whole number of steps (lots to the lot step: 0.0536 → 0.05 at 0.01). */
export function floorToStep(x, step) {
    return D.from(x).div(step, 'down').round(0, 'down').mul(step);
}
/**
 * Qty an order sized in USD opens: the lots it buys at this price, rounded down to the lot step, so it
 * holds a volume an order placed in lots could (0.05 lots, never 0.0536). Zero when under one step.
 */
export function qtyFromNotionalInLots(notional, price, contractSize, lotSize, lotStep) {
    const lots = qtyToLots(qtyFromNotional(notional, price, contractSize), lotSize);
    return lotsToQty(floorToStep(lots, lotStep), lotSize);
}
export function notionalOf(qty, price, contractSize) {
    return D.from(qty).mul(price).mul(contractSize).round(MONEY_DP, 'half_up');
}
export function initialMargin(notional, leverage) {
    return D.from(notional).div(leverage, 'ceil').round(MONEY_DP, 'ceil');
}
/** Why a leverage range is invalid (null when it is valid): whole numbers with 1 ≤ min ≤ default ≤ max ≤ 1000. */
export function leverageRangeError(r) {
    const { minLeverage: lo, defaultLeverage: d, maxLeverage: hi } = r;
    if (![lo, d, hi].every((v) => Number.isInteger(v) && v >= 1 && v <= 1000))
        return 'Leverage must be a whole number from 1 to 1000';
    if (lo > d)
        return `Min leverage (${lo}x) is above the default (${d}x)`;
    if (d > hi)
        return `Default leverage (${d}x) is above the max (${hi}x)`;
    return null;
}
/** The range an instrument uses: each override, or the category's value where there is none. */
export function resolveLeverage(o, category) {
    return { minLeverage: o.min ?? category.minLeverage, defaultLeverage: o.default ?? category.defaultLeverage, maxLeverage: o.max ?? category.maxLeverage };
}
/** `lev` moved into the instrument's min…max range. */
export function clampLeverage(lev, r) {
    return Math.max(r.minLeverage, Math.min(r.maxLeverage, lev));
}
export function feeFor(notional, feeRate) {
    return D.from(notional).mul(feeRate, 'ceil').round(MONEY_DP, 'ceil');
}
/** Fee charged when a position opens: the rate on its notional plus the flat USD fee. Closing charges only the rate. */
export function openFeeFor(notional, feeRate, feeFlat = 0) {
    return feeFor(notional, feeRate).add(feeFlat);
}
/** Unrealized / realized PnL at `exitPrice`. Rounded toward -inf (to the cent-of-a-cent). */
export function pnl(side, entry, exitPrice, qty, contractSize) {
    const diff = side === 'long' ? D.from(exitPrice).sub(entry) : D.from(entry).sub(exitPrice);
    return diff.mul(qty, 'floor').mul(contractSize, 'floor').round(MONEY_DP, 'floor');
}
export function positionEquity(margin, upnl, swap) {
    return D.from(margin).add(upnl).add(swap);
}
/** margin level = equity / margin (1 = 100 %). */
export function marginLevel(margin, upnl, swap) {
    const m = D.from(margin);
    if (m.isZero())
        return D.ZERO;
    return positionEquity(m, upnl, swap).div(m, 'floor');
}
/**
 * Account free margin = equity − used margin − pending order holds (equity = balance + credit + unrealized PnL).
 * Display figure; order checks use the server's balance-based `available`.
 */
export function freeMargin(equity, marginUsed, orderHold) {
    return D.from(equity).sub(marginUsed).sub(orderHold);
}
/**
 * Margin each open position of one account on one instrument holds under hedging.
 * Buy and sell volume offset: the matched volume holds `hedgedRate` of its margin, split evenly
 * between the two sides (0 = matched volume is free), and the uncovered volume of the larger side
 * holds full margin, shared pro rata across that side's positions. Without both sides open every
 * position holds its own margin.
 */
export function hedgedMargins(legs, hedgedRate = 0) {
    let long = D.ZERO;
    let short = D.ZERO;
    for (const l of legs) {
        if (l.side === 'long')
            long = long.add(l.qty);
        else
            short = short.add(l.qty);
    }
    const matched = D.min(long, short);
    const rate = D.from(hedgedRate);
    return legs.map((l) => {
        const m = D.from(l.margin);
        if (matched.isZero())
            return m;
        const matchedMargin = m.mul(matched).div(l.side === 'long' ? long : short, 'floor');
        const held = m.sub(matchedMargin).add(matchedMargin.mul(rate).div(TWO, 'ceil')).round(MONEY_DP, 'ceil');
        return D.min(m, held);
    });
}
/**
 * Account stop-out: margin level = equity / margin used, with equity = cash + credit + unrealized
 * P&L incl. swap. With no margin used (fully hedged) it stops out when equity reaches zero.
 */
export function accountStopOut(equity, marginUsed, level) {
    const m = D.from(marginUsed);
    return m.isPos() ? D.from(equity).lte(m.mul(level)) : !D.from(equity).isPos();
}
/** Highest leverage a client may use on an instrument: its maximum, or the client's cap (set by staff) when lower. */
export function leverageCap(instrumentMax, userMax) {
    return userMax == null ? instrumentMax : Math.min(instrumentMax, userMax);
}
/** Account margin level = equity / margin used (1 = 100 %); null with no margin used. */
export function accountMarginLevel(equity, marginUsed) {
    const m = D.from(marginUsed);
    return m.isPos() ? D.from(equity).div(m, 'floor') : null;
}
/**
 * Estimated stop-out price on one instrument under cross margin: the price at which the account's
 * equity falls to `level` × margin used (to zero with no margin used), the account's positions on
 * other instruments held at their current prices. `netUnits` is the account's net exposure on the
 * instrument in USD per point (Σ qty × usdPerPoint, longs positive, shorts negative), so offsetting
 * buy and sell volume moves together. `price` is the current price it is measured from (the
 * position's close-side price). p = price − (equity − level·marginUsed) / netUnits. Null when the
 * instrument is fully hedged (no net exposure).
 */
export function liquidationPrice(price, netUnits, equity, marginUsed, level, priceDp = 8) {
    const net = D.from(netUnits);
    if (net.isZero())
        return null;
    const m = D.from(marginUsed);
    const buffer = D.from(equity).sub(m.isPos() ? m.mul(level) : D.ZERO);
    const p = D.from(price).sub(buffer.div(net));
    return (p.isNeg() ? D.ZERO : p).round(priceDp, net.isPos() ? 'ceil' : 'floor');
}
/** Signed net exposure of positions on one instrument, in USD per point (longs +, shorts −). */
export function netUnits(positions, usdPerPt) {
    let net = D.ZERO;
    for (const p of positions)
        net = p.side === 'long' ? net.add(p.qty) : net.sub(p.qty);
    return net.mul(usdPerPt);
}
/** Largest notional that `available` can open including the opening fee (rate and flat fee). */
export function maxNotional(available, leverage, feeRate, feeFlat = 0) {
    const a = D.from(available);
    if (!a.isPos())
        return D.ZERO;
    const lev = D.from(leverage);
    // Margin and fee are each rounded up by at most one money unit; reserve both.
    const usable = a.sub(feeFlat).sub(D.from('0.00000002'));
    if (!usable.isPos())
        return D.ZERO;
    return usable.mul(lev).div(D.ONE.add(lev.mul(feeRate)), 'down').round(MONEY_DP, 'down');
}
// ---- TP / SL as price % (distance from entry) ----
export function tpPriceFromPct(side, entry, pct) {
    const f = D.from(pct).div(HUNDRED);
    return side === 'long' ? D.from(entry).mul(D.ONE.add(f)) : D.from(entry).mul(D.ONE.sub(f));
}
export function slPriceFromPct(side, entry, pct) {
    const f = D.from(pct).div(HUNDRED);
    return side === 'long' ? D.from(entry).mul(D.ONE.sub(f)) : D.from(entry).mul(D.ONE.add(f));
}
export function tpPctFromPrice(side, entry, price) {
    const r = D.from(price).div(entry);
    return (side === 'long' ? r.sub(D.ONE) : D.ONE.sub(r)).mul(HUNDRED);
}
export function slPctFromPrice(side, entry, price) {
    const r = D.from(price).div(entry);
    return (side === 'long' ? D.ONE.sub(r) : r.sub(D.ONE)).mul(HUNDRED);
}
/** Returns an error message, or null if TP/SL are on the correct side of `ref`. */
export function validateTpSl(side, ref, tp, sl) {
    const r = D.from(ref);
    if (tp && !tp.isPos())
        return 'TP price must be positive';
    if (sl && !sl.isPos())
        return 'SL price must be positive';
    if (side === 'long') {
        if (tp && tp.lte(r))
            return 'TP must be above the current price for a long';
        if (sl && sl.gte(r))
            return 'SL must be below the current price for a long';
    }
    else {
        if (tp && tp.gte(r))
            return 'TP must be below the current price for a short';
        if (sl && sl.lte(r))
            return 'SL must be above the current price for a short';
    }
    return null;
}
// ---- Trigger checks (evaluated on the close side for TP/SL, open side for limits) ----
export function tpHit(side, q, tp) {
    return side === 'long' ? q.bid.gte(tp) : q.ask.lte(tp);
}
export function slHit(side, q, sl) {
    return side === 'long' ? q.bid.lte(sl) : q.ask.gte(sl);
}
/** Buy limit fills when ask ≤ limit; sell limit fills when bid ≥ limit. */
export function limitHit(side, q, limit) {
    return side === 'long' ? q.ask.lte(limit) : q.bid.gte(limit);
}
/** Buy stop fills when ask ≥ stop; sell stop fills when bid ≤ stop. */
export function stopHit(side, q, stop) {
    return side === 'long' ? q.ask.gte(stop) : q.bid.lte(stop);
}
export function pendingHit(type, side, q, price) {
    return type === 'stop' ? stopHit(side, q, price) : limitHit(side, q, price);
}
/**
 * The pending order a price makes against the current open-side price (ask for a buy, bid for a sell).
 * A buy below the ask is a limit and above it a stop; a sell above the bid is a limit and below it a
 * stop. Null when the price is the open-side price itself (that is a market order).
 */
export function pendingKind(side, q, price) {
    const ref = side === 'long' ? q.ask : q.bid;
    if (ref.eq(price))
        return null;
    const below = ref.gt(price);
    return side === 'long' ? (below ? 'limit' : 'stop') : below ? 'stop' : 'limit';
}
/** "Buy", "Sell Limit", "Buy Stop"… */
export function orderTypeLabel(side, type) {
    return (side === 'long' ? 'Buy' : 'Sell') + (type === 'limit' ? ' Limit' : type === 'stop' ? ' Stop' : '');
}
/**
 * Why `price` cannot be a pending order of `type` against the quote, or null when it can. A price equal
 * to the open-side price is accepted either way (it triggers on the next tick). `fmtRef` formats the
 * quote price for the message.
 */
export function pendingPriceError(side, type, q, price, fmtRef) {
    const kind = pendingKind(side, q, price);
    if (!kind || kind === type)
        return null;
    const ref = fmtRef(side === 'long' ? q.ask : q.bid);
    const refName = side === 'long' ? 'Ask' : 'Bid';
    const above = (side === 'long') === (type === 'stop');
    return `A ${orderTypeLabel(side, type)} must be ${above ? 'above' : 'below'} the current ${refName} (${ref}).`;
}
/** Swap P&L for one charge (signed; negative = client pays). Rates are percent. */
export function swapCharge(side, notionalAtMark, longRatePct, shortRatePct, multiplier = 1) {
    const rate = side === 'long' ? D.from(longRatePct) : D.from(shortRatePct);
    return D.from(notionalAtMark).mul(rate).div(HUNDRED).mul(multiplier).round(MONEY_DP, 'floor');
}
/**
 * Settle a closing position against its isolated margin.
 * The client can never lose more than the position's margin; the house absorbs any gap.
 * `margin` null = a hedged position: its P&L is settled in full against the account (the account's
 * negative-balance protection applies instead).
 */
export function settleClose(margin, realizedPnl, swap, closeFee) {
    const p = D.from(realizedPnl);
    const s = D.from(swap);
    if (margin === null) {
        const fee = D.from(closeFee);
        return { pnlEntry: p, swapEntry: s, feeCharged: fee, walletDelta: p.add(s).sub(fee) };
    }
    const m = D.from(margin);
    const equity = m.add(p).add(s);
    if (!equity.isPos()) {
        // Whole margin lost. Book swap as-is and put the remainder on PnL so entries sum to −margin.
        return { pnlEntry: m.neg().sub(s), swapEntry: s, feeCharged: D.ZERO, walletDelta: m.neg() };
    }
    const fee = D.min(closeFee, equity);
    return { pnlEntry: p, swapEntry: s, feeCharged: fee, walletDelta: p.add(s).sub(fee) };
}
//# sourceMappingURL=trading-math.js.map